import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaBoxOpen, FaTruck, FaReceipt } from 'react-icons/fa';
import { formatCurrency } from '../../utils/cartTotals';

const PAYMENT_LABEL = {
  COD: 'Cash on Delivery',
  UPI: 'UPI',
  CARD: 'Credit / Debit Card',
  NETBANKING: 'Net Banking',
  WALLET: 'Wallet',
  ONLINE: 'Online Payment',
};

const OrderConfirmed = ({ order }) => {
  const navigate = useNavigate();

  const {
    order_number,
    payment_method = 'COD',
    payment_status = 'UNPAID',
    status = 'PENDING',
    total,
    form = {},
    subtotal,
    delivery,
    gstTotal,
  } = order || {};

  const deliveryAddress = [form.addressLine1, form.city, form.state]
    .filter(Boolean)
    .join(', ');

  const isCOD = payment_method === 'COD';
  const isPaid = payment_status === 'PAID';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-primary-50 via-white to-emerald-50 px-4 py-16">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-8 flex flex-col items-center">
          <div className="relative mb-4">
            <div className="absolute inset-0 animate-ping rounded-full bg-emerald-200 opacity-60" />
            <div className="absolute -inset-3 rounded-full border border-emerald-100" />
            <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-emerald-500 shadow-2xl shadow-primary-500/40">
              <FaCheckCircle className="h-14 w-14 text-white" />
            </div>
            <span className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md">
              <FaReceipt className="h-3.5 w-3.5 text-primary-600" />
            </span>
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">Order Placed!</h1>
          <p className="mt-1 text-center text-gray-500">
            Thank you{form.fullName ? `, ${form.fullName}` : ''}! Your order has been received.
          </p>
        </div>

        <div className="mb-4 overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-lg">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3">
            <p className="text-xs font-bold uppercase tracking-widest text-primary-100">Order Number</p>
            <p className="text-xl font-extrabold text-white">{order_number || '-'}</p>
          </div>

          <div className="divide-y divide-gray-50 px-5 py-1">
            <div className="flex items-center justify-between py-3">
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <FaReceipt className="h-3.5 w-3.5 text-primary-400" /> Payment
              </span>
              <div className="text-right">
                <span className="text-sm font-bold text-gray-800">
                  {PAYMENT_LABEL[payment_method] || payment_method}
                </span>
                <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  isPaid
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {isPaid ? 'PAID' : payment_status}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between py-3">
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <FaBoxOpen className="h-3.5 w-3.5 text-primary-400" /> Status
              </span>
              <span className="rounded-full bg-amber-50 px-3 py-0.5 text-xs font-bold text-amber-700">
                {status}
              </span>
            </div>

            {deliveryAddress && (
              <div className="flex items-start justify-between py-3">
                <span className="flex items-center gap-2 text-sm text-gray-500">
                  <FaTruck className="mt-0.5 h-3.5 w-3.5 text-primary-400" /> Deliver to
                </span>
                <span className="ml-4 text-right text-sm font-semibold text-gray-700">
                  {deliveryAddress}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-50 bg-gray-50 px-5 py-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Order Summary</p>
          </div>
          <div className="space-y-2.5 px-5 py-4 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold">{formatCurrency(subtotal ?? total ?? 0)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Delivery</span>
              <span className={`font-semibold ${(delivery ?? 0) === 0 ? 'text-primary-600' : ''}`}>
                {(delivery ?? 0) === 0 ? 'FREE' : formatCurrency(delivery)}
              </span>
            </div>
            {(gstTotal ?? 0) > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>GST</span>
                <span className="font-semibold">{formatCurrency(gstTotal)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-gray-100 pt-2.5 text-base font-extrabold text-gray-900">
              <span>Total</span>
              <span className="text-primary-600">{formatCurrency(total ?? 0)}</span>
            </div>
          </div>
        </div>

        {isCOD && (
          <div className="mb-5 rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4">
            <p className="text-sm font-bold text-orange-800">Cash on Delivery</p>
            <p className="mt-1 text-xs leading-5 text-orange-700">
              Please keep exact change ready. Our delivery partner will collect{' '}
              <strong>{formatCurrency(total ?? 0)}</strong> at your doorstep.
            </p>
          </div>
        )}

        {!isCOD && !isPaid && (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-sm font-bold text-amber-800">Payment Pending</p>
            <p className="mt-1 text-xs leading-5 text-amber-700">
              Your order is reserved. Complete payment from My Orders if the payment window closed.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={() => navigate('/account/orders')}
            className="flex-1 rounded-2xl border-2 border-primary-600 py-3.5 text-sm font-bold text-primary-600 transition hover:bg-primary-50"
          >
            View My Orders
          </button>
          <button
            onClick={() => navigate('/products')}
            className="flex-1 rounded-2xl bg-primary-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary-600/30 transition hover:bg-primary-700"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmed;
